import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, AlertTriangle, FileDown, Mail } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useLivePoll } from "@/hooks/useLivePoll";
import ReportDocumentView from "@/components/ReportDocumentView";
import EmailModal from "@/components/EmailModal";
import { downloadReportPdf } from "@/lib/reportPdf";

/**
 * HandoverDetailPage — full-page render of a Shift Handover record
 * (Josh Feb 23: "when you click on that breakdown / handover it opens up
 * a full page so you can easily see it"). Uses the shared certificate
 * layout via <ReportDocumentView type="handover"/> so the look matches
 * PPF / Hazard / Breakdown exactly.
 */
export default function HandoverDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [hd, setHd] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(true);
  const [emailOpen, setEmailOpen] = useState(false);

  const reload = async () => {
    setErr("");
    try {
      const { data } = await api.get(`/handovers/${id}`);
      setHd(data);
    } catch (ex) {
      if (ex?.response?.status === 404 || ex?.response?.status === 405) {
        try {
          const { data } = await api.get("/handovers");
          const arr = Array.isArray(data) ? data : data?.items || [];
          const hit = arr.find((x) => (x.id || x._id) === id);
          if (hit) { setHd(hit); return; }
          setErr("Handover not found.");
        } catch (ex2) { setErr(apiError(ex2)); }
      } else {
        setErr(apiError(ex));
      }
    } finally { setBusy(false); }
  };
  useEffect(() => { reload(); }, [id]);
  useLivePoll(reload, 5000, true);

  return (
    <div className="min-h-screen" data-testid="handover-detail-page">
      <div className="px-5 pt-4 pb-3 flex items-center justify-between gap-3 flex-wrap">
        <button
          onClick={() => nav(-1)}
          className="btn btn-sm btn-ghost"
          data-testid="handover-page-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        {hd && (
          <div className="flex items-center gap-2">
            <button
              className="btn btn-sm btn-ghost"
              onClick={() => downloadReportPdf(hd, "Handover")}
              data-testid="handover-page-pdf"
              title="Download PDF"
            >
              <FileDown className="w-4 h-4" /> PDF
            </button>
            <button
              className="btn btn-sm btn-ghost"
              onClick={() => setEmailOpen(true)}
              data-testid="handover-page-email"
              title="Email report"
            >
              <Mail className="w-4 h-4" /> Email
            </button>
          </div>
        )}
      </div>

      <div className="px-5 pb-8">
        {busy && !hd && (
          <div className="panel-flat p-10 flex items-center justify-center gap-3 text-muted">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading handover…
          </div>
        )}
        {err && !hd && (
          <div className="pill pill-red !text-sm !py-2 !px-3 justify-start">
            <AlertTriangle className="w-3.5 h-3.5" /> {err}
          </div>
        )}
        {hd && <ReportDocumentView type="handover" item={hd} />}
      </div>

      {emailOpen && hd && (
        <EmailModal open kind="Handover" item={hd} onClose={() => setEmailOpen(false)} />
      )}
    </div>
  );
}
